import { BooleanField } from "../blocks/fields/BooleanField";
import { FieldValue } from "../blocks/fields/FieldValue";
import { MoneyField } from "../blocks/fields/MoneyField";
import { MultiSelectField } from "../blocks/fields/MultiSelectField";
import { NumberField } from "../blocks/fields/NumberField";
import { PicklistField } from "../blocks/fields/PicklistField";
import { PicklistOption } from "../blocks/fields/PicklistField.types";
import { SearchableSelect } from "../blocks/fields/SearchableSelect";
import { TextField } from "../blocks/fields/TextField";
import { useLookupOptions } from "../hooks/useLookupOptions";
import { isListOperator, operatorNeedsValue } from "./advancedSearchRules";
import { RuleDateValue } from "./RuleDateValue";
import { RuleValueEditorProps } from "./RuleValueEditor.types";

type Lookup = { entityName?: string };

/** The field's own words where it has them (Active/Inactive), otherwise Yes or No. */
function getToggleLabel(rule: RuleValueEditorProps["rule"], on: boolean) {
  const own = on ? rule.trueLabel : rule.falseLabel;

  return own ? String(own) : on ? "Yes" : "No";
}

const toNumber = (value: unknown) =>
  value === "" || value === null || value === undefined ? null : Number(value);

/** The control for a rule's value, which depends on the field's type and the operator. */
export function RuleValueEditor({ rule, id, error, onChange }: RuleValueEditorProps) {
  const lookup = rule.lookup as Lookup | undefined;
  const { options: lookupOptions } = useLookupOptions(lookup?.entityName);
  const type = rule.type ?? "text";

  if (!operatorNeedsValue(rule.operator)) {
    return null;
  }

  if (type === "datetime") {
    return <RuleDateValue {...{ rule, id, error, onChange }} />;
  }

  if (type === "bit") {
    return (
      <BooleanField
        label={getToggleLabel(rule, Boolean(rule.value))}
        name={id}
        value={Boolean(rule.value)}
        onChange={onChange}
        error={error}
        className="mt-4 pt-2"
      />
    );
  }

  if (type === "int") {
    return (
      <NumberField
        label="Value"
        name={id}
        value={toNumber(rule.value)}
        onChange={(value) => onChange(value === null ? "" : String(value))}
        error={error}
      />
    );
  }

  if (type === "money") {
    return (
      <MoneyField
        label="Value"
        name={id}
        value={toNumber(rule.value)}
        onChange={(value) => onChange(value === null ? "" : String(value))}
        error={error}
      />
    );
  }

  if (type === "select" || type === "picklist") {
    const options: PicklistOption[] = rule.enum
      ? Object.entries(rule.enum as Record<string, string | number>).map(
          ([Value, Key]) => ({ Key, Value })
        )
      : lookupOptions;

    if (isListOperator(rule.operator)) {
      return (
        <MultiSelectField
          label="Value"
          name={id}
          value={Array.isArray(rule.value) ? rule.value : []}
          options={options.map(({ Key, Value }) => ({ value: Key, label: Value }))}
          onChange={onChange}
          error={error}
        />
      );
    }

    const keyOf = (selected: string | null) =>
      options.find((option) => String(option.Key) === selected)?.Key ?? "";

    return lookup ? (
      <FieldValue label="Value" htmlFor={id} error={error}>
        <SearchableSelect
          id={id}
          value={rule.value === "" ? null : String(rule.value)}
          options={options.map(({ Key, Value }) => ({ value: String(Key), label: Value }))}
          onChange={(selected) => onChange(keyOf(selected))}
          clearable
          ariaInvalid={Boolean(error)}
        />
      </FieldValue>
    ) : (
      <PicklistField
        label="Value"
        name={id}
        value={rule.value === "" ? null : (rule.value as string | number)}
        options={options}
        onChange={(selected) => onChange(keyOf(selected))}
        error={error}
      />
    );
  }

  return (
    <TextField
      label="Value"
      name={id}
      value={String(rule.value ?? "")}
      onChange={onChange}
      error={error}
    />
  );
}
